import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { applyEach, form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalCheckbox,
  SignalDropdown,
  SignalFormDirective,
  SignalSwitch,
  SignalTextField,
  OverlayService,
} from '@vallift/ngl';
import {
  FormField,
  SingleChoiceGridConfig,
  SingleChoiceGridResponseType,
  readSingleChoiceGridConfig,
} from '../../../../models/built-form';
import { SingleChoiceGridType } from '@shared/components/single-choice-grid/single-choice-grid';
import { inject } from '@angular/core';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';

import type { FormFieldDataLibraryCollectionRef } from '../../../../models/built-form';

import {
  LinkCollectionButtonComponent,
  type LinkedCollectionResult,
} from '../../../link-collection-button/link-collection-button';

import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
} from '../../../collection-column-picker-overlay/collection-column-picker-overlay';

import {
  resolveCollectionDropdownItems,
  type CollectionSelectOption,
} from '../../../../utils/resolve-collection-dropdown-items';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { constants } from '@core/constants/constants';
import {
  createGridChoice,
  defaultGridChoices,
  type GridChoiceOption,
} from '../../../../utils/grid-choice-option';

interface SingleChoiceGridConfigModel {
  label: string;
  helpText: string;
  responseType: SingleChoiceGridType;
  choice: GridChoiceOption[];
  items: string[];
  allowAdditionalRows: boolean;
  addRowButtonLabel: string;
  required: boolean;
}

const DEFAULT_CHOICE_LABELS = ['Yes', 'No'] as const;
const DEFAULT_ITEMS = ['Option 1', 'Option 2', 'Option 3'];

interface ColumnOption {
  key: string;
  label: string;
}

@Component({
  selector: 'app-single-choice-grid-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalSwitch,
    SignalFormDirective,
    SignalButton,
    AppSvgIconComponent,
    LinkCollectionButtonComponent,
    CdkDragPlaceholder,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
  ],
  templateUrl: './single-choice-grid-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleChoiceGridConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly linkedCollection = signal<FormFieldDataLibraryCollectionRef | null>(null);

  readonly isCollectionLinked = computed(() => this.linkedCollection() !== null);

  readonly collectionColumns = signal<ColumnOption[]>([]);
  readonly collectionItems = signal<CollectionSelectOption[]>([]);

  readonly showAsLabel = computed(() => {
    const linked = this.linkedCollection();

    if (!linked?.displayFieldKey) {
      return '';
    }

    return (
      linked.displayFieldLabel ||
      this.collectionColumns().find((column) => column.key === linked.displayFieldKey)?.label ||
      linked.displayFieldKey
    );
  });
  readonly effectiveResponseType = computed<SingleChoiceGridType>(() => {
    if (
      this.previewChoices().length >
      constants.singleChoiceGridMaxButtonOptions
    ) {
      return 'dropdown';
    }

    return this.model().responseType;
  });

  private lastLoadedCollectionId: string | null = null;

  readonly responseTypeChoices: {
    value: SingleChoiceGridType;
    label: string;
  }[] = [
    { value: 'button', label: 'Button' },
    { value: 'radio', label: 'Radio' },
    { value: 'dropdown', label: 'Dropdown' },
  ];

  readonly model = signal<SingleChoiceGridConfigModel>({
    label: 'Response Matrix',
    helpText: '',
    responseType: 'button',
    choice: defaultGridChoices(DEFAULT_CHOICE_LABELS),
    items: [...DEFAULT_ITEMS],
    allowAdditionalRows: false,
    addRowButtonLabel: '+ Add another item',
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);

    applyEach(schema.choice, (option) => {
      required(option.label);
    });

    applyEach(schema.items, (item) => {
      required(item);
    });
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const field = this.field();

      if (!field) return;

      if (field.id === this.lastFieldId) return;

      this.lastFieldId = field.id;
      this.suppressEmit = true;

      const config = readSingleChoiceGridConfig(field);
      const collection = config.itemsDataLibraryCollection;

      this.linkedCollection.set(
        collection?.dataLibraryId
          ? {
            dataLibraryId: collection.dataLibraryId,
            displayFieldKey: collection.displayFieldKey,
            displayFieldLabel: collection.displayFieldLabel,
            label: collection.label,
            key: collection.key,
          }
          : null,
      );

      this.model.set({
        label: field.label ?? 'Response Matrix',
        helpText: field.helpText ?? '',
        responseType: this.normalizeResponseType(config.responseType),
        choice: config.choice?.length
          ? config.choice.map((option) => ({ ...option }))
          : defaultGridChoices(DEFAULT_CHOICE_LABELS),
        items: config.items?.length ? [...config.items] : [...DEFAULT_ITEMS],
        allowAdditionalRows: config.allowAdditionalRows ?? false,
        addRowButtonLabel: config.addRowButtonLabel ?? '+ Add another item',
        required: field.validation?.required ?? field.required ?? false,
      });
    });

    effect(() => {
      const choiceCount = this.previewChoices().length;
      const maxButtonOptions = constants.singleChoiceGridMaxButtonOptions;
      const currentType = this.model().responseType;

      if (choiceCount > maxButtonOptions && currentType !== 'dropdown') {
        this.model.update((model) => ({
          ...model,
          responseType: 'dropdown',
        }));
      }
    });

    effect(() => {
      const collection = this.linkedCollection();

      if (!collection?.dataLibraryId) {
        this.collectionColumns.set([]);
        this.collectionItems.set([]);
        this.lastLoadedCollectionId = null;
        return;
      }

      if (this.lastLoadedCollectionId === collection.dataLibraryId) {
        return;
      }

      this.lastLoadedCollectionId = collection.dataLibraryId;
      void this.loadCollectionMetadata(collection.dataLibraryId);
    });

    effect(() => {
      const collection = this.linkedCollection();

      if (!collection?.dataLibraryId || !collection.displayFieldKey) {
        this.collectionItems.set([]);
        return;
      }

      void this.loadCollectionItems(
        collection.dataLibraryId,
        collection.displayFieldKey,
      );
    });

    effect(() => {
      const draft = this.model();

      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }

      const field = untracked(() => this.field());

      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const choice = draft.choice
        .map((option) => ({
          id: option.id,
          label: option.label.trim(),
        }))
        .filter((option) => option.label.length > 0);
      const items = draft.items.map((item) => item.trim()).filter(Boolean);
      const linkedCollection = this.linkedCollection();
      const nextChoice =
        choice.length > 0 ? choice : defaultGridChoices(DEFAULT_CHOICE_LABELS);

      const config: SingleChoiceGridConfig = {
        ...readSingleChoiceGridConfig(field),
        responseType: draft.responseType,
        items,
        choice: nextChoice,
        options: nextChoice,
        allowAdditionalRows: draft.allowAdditionalRows,
        addRowButtonLabel: draft.addRowButtonLabel,
        ...(linkedCollection
          ? {
            itemsDataLibraryCollection: linkedCollection,
          }
          : {}),
      };

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim() || 'Response Matrix',
        helpText: draft.helpText.trim() || '',
        options: items.length > 0 ? items : [...DEFAULT_ITEMS],
        config,
        validation: {
          required: draft.required,
        },
        required: draft.required,
      });
    });
  }

  private loadCollectionMetadata(collectionId: string): void {
    this.workspaceApi.getDataLibraryCollection(collectionId).subscribe({
      next: (response) => {
        const fields = response.data.fields ?? [];

        this.collectionColumns.set(
          fields.map((field) => ({
            key: field.key,
            label: field.name || field.key,
          })),
        );
      },
      error: () => {
        this.collectionColumns.set([]);
      },
    });
  }
  private loadCollectionItems(collectionId: string, displayFieldKey: string): void {
    resolveCollectionDropdownItems(this.workspaceApi, collectionId, displayFieldKey).subscribe({
      next: (items) => {
        this.collectionItems.set(items);

        const employeeNames = items.map((item) => item.display.trim()).filter(Boolean);

        this.model.update((model) => ({
          ...model,
          items: employeeNames.length ? employeeNames : [...DEFAULT_ITEMS],
        }));
      },
      error: () => {
        this.collectionItems.set([]);
      },
    });
  }

  onCollectionSelected(result: LinkedCollectionResult): void {
    this.suppressEmit = false;
    this.lastLoadedCollectionId = null;

    this.linkedCollection.set({
      dataLibraryId: result.id,
      displayFieldKey: result.displayFieldKey,
      displayFieldLabel: result.displayFieldLabel,
      label: result.name,
      key: result.key,
    });
  }

  onDisplayFieldKeyChange(value: string): void {
    const collection = this.linkedCollection();

    if (!collection) {
      return;
    }

    const column = this.collectionColumns().find((item) => item.key === value);

    this.linkedCollection.set({
      ...collection,
      displayFieldKey: value,
      displayFieldLabel: column?.label ?? value,
    });
  }

  detachLinkedCollection(): void {
    const items = this.collectionItems()
      .map((item) => item.display.trim())
      .filter(Boolean);

    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionItems.set([]);
    this.lastLoadedCollectionId = null;

    this.model.update((model) => ({
      ...model,
      items: items.length ? items : [...DEFAULT_ITEMS],
    }));
  }

  removeLinkedCollection(): void {
    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionItems.set([]);
    this.lastLoadedCollectionId = null;

    this.model.update((model) => ({
      ...model,
      items: [...DEFAULT_ITEMS],
    }));
  }

  readonly previewChoices = () =>
    this.model()
      .choice.map((choice) => choice.label.trim())
      .filter(Boolean);

  setResponseType(value: SingleChoiceGridResponseType): void {
    if (value !== 'button' && value !== 'radio' && value !== 'dropdown') {
      return;
    }

    this.model.update((model) => ({
      ...model,
      responseType: value,
    }));
  }

  addChoice(): void {
    this.model.update((model) => ({
      ...model,
      choice: [...model.choice, createGridChoice('')],
    }));
  }

  removeChoice(index: number): void {
    this.model.update((model) => {
      if (model.choice.length <= 2) {
        return model;
      }

      return {
        ...model,
        choice: model.choice.filter((_, i) => i !== index),
      };
    });
  }

  addItem(): void {
    this.model.update((model) => ({
      ...model,
      items: [...model.items, ''],
    }));
  }

  removeItem(index: number): void {
    this.model.update((model) => {
      if (model.items.length <= 1) {
        return model;
      }

      return {
        ...model,
        items: model.items.filter((_, i) => i !== index),
      };
    });
  }

  setAddRowButtonLabel(value: string): void {
    this.model.update((model) => ({
      ...model,
      addRowButtonLabel: value,
    }));
  }

  private normalizeResponseType(value: SingleChoiceGridResponseType | undefined): SingleChoiceGridResponseType {
    if (value === 'button' || value === 'radio' || value === 'dropdown') {
      return value;
    }

    return 'button';
  }

  changeShowAsColumn(): void {
    const linked = this.linkedCollection();

    if (!linked) return;

    const dialogRef = this.overlayService.openModal(CollectionColumnPickerOverlayComponent, {
      fitContent: true,
      panelClass: ['w-[22rem]', 'max-w-[calc(100%-2rem)]'],
      data: {
        collectionId: linked.dataLibraryId,
        collectionName: linked.label,
        selectedDisplayFieldKey: linked.displayFieldKey,
      },
    });

    dialogRef.closed.subscribe((result: CollectionColumnPickerResult | null | undefined) => {
      if (!result?.displayFieldKey) return;

      this.linkedCollection.update((current) => {
        if (!current) return current;

        return {
          ...current,
          displayFieldKey: result.displayFieldKey,
          displayFieldLabel: result.displayFieldLabel,
        };
      });
    });
  }

  onChoiceDrop(event: CdkDragDrop<GridChoiceOption[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const choice = [...model.choice];

      moveItemInArray(choice, event.previousIndex, event.currentIndex);

      return {
        ...model,
        choice,
      };
    });
  }

  onItemDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const items = [...model.items];

      moveItemInArray(items, event.previousIndex, event.currentIndex);

      return {
        ...model,
        items,
      };
    });
  }
}
